import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import { AppShell } from '@/components/layout/AppShell';
import { AdminSectionTabs } from '@/components/admin/AdminSectionTabs';
import { SubscribersSubNav } from '@/components/admin/SubscribersSubNav';
import { Button } from '@/components/ui/Button';
import { Chip } from '@/components/ui/Chip';
import { adminAutomationsService } from '@/services/admin-automations.service';
import { ApiError } from '@/services/http';
import type { AutomationView } from '@/types/api';

/** "3pm" rather than "15" — the builder stores an hour, admins think in clock time. */
export function formatSendHour(hour: number): string {
  const suffix = hour < 12 ? 'am' : 'pm';
  const h = hour % 12 === 0 ? 12 : hour % 12;
  return `${h}${suffix}`;
}

export function AdminAutomationsPage() {
  const navigate = useNavigate();
  const [automations, setAutomations] = useState<AutomationView[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    adminAutomationsService
      .list()
      .then(setAutomations)
      .catch((err) =>
        toast.error(err instanceof ApiError ? err.message : 'Could not load automations.'),
      )
      .finally(() => setLoading(false));
  }, []);

  return (
    <AppShell mode="parent">
      <div className="flex flex-col gap-6">
        <AdminSectionTabs />
        <SubscribersSubNav />

        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="or-kicker text-or-orange-600">Admin · Subscribers</div>
            <h1 className="mt-1 font-display text-[22px] font-bold text-or-slate-900 sm:text-[26px]">
              Automations
            </h1>
            <p className="mt-1 max-w-xl text-[13px] text-or-slate-600">
              Send a template automatically when subscribers match a set of rules. Each automation
              checks hourly and sends at most once per subscriber, ever.
            </p>
          </div>
          <Button
            variant="primary"
            size="md"
            onClick={() => navigate('/admin/subscribers/automations/new')}
          >
            <Plus className="h-4 w-4" aria-hidden />
            New automation
          </Button>
        </div>

        <div className="overflow-x-auto rounded-2xl border border-or-slate-200 bg-white">
          <table className="w-full text-left text-[13px]">
            <thead className="bg-or-slate-50 text-[11px] uppercase tracking-[0.08em] text-or-slate-500">
              <tr>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">List</th>
                <th className="px-4 py-3">Rules</th>
                <th className="px-4 py-3">Sends at</th>
                <th className="px-4 py-3">Last run</th>
              </tr>
            </thead>
            <tbody>
              {loading && automations.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-or-slate-500">
                    Loading…
                  </td>
                </tr>
              ) : automations.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-or-slate-500">
                    No automations yet.
                  </td>
                </tr>
              ) : (
                automations.map((a) => (
                  <tr
                    key={a.id}
                    onClick={() => navigate(`/admin/subscribers/automations/${a.id}`)}
                    className="cursor-pointer border-t border-or-slate-100 hover:bg-or-slate-50"
                  >
                    <td className="px-4 py-3 font-semibold text-or-slate-900">{a.name}</td>
                    <td className="px-4 py-3">
                      {a.enabled ? (
                        <Chip variant="live">Live</Chip>
                      ) : (
                        <Chip variant="default">Paused</Chip>
                      )}
                    </td>
                    <td className="px-4 py-3 text-or-slate-700">{a.listName ?? '—'}</td>
                    <td className="px-4 py-3 text-or-slate-700">{a.conditions.length}</td>
                    <td className="px-4 py-3 text-or-slate-700">
                      {formatSendHour(a.sendHour)}{' '}
                      <span className="text-or-slate-400">{a.timezone}</span>
                    </td>
                    <td className="px-4 py-3 text-or-slate-400">
                      {a.lastRunAt ? new Date(a.lastRunAt).toLocaleDateString() : 'Never'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </AppShell>
  );
}
